import { round } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateMultipleDimensions } from '../../features/components/componentSlice'
import { chooseMaxPixels, convertToPerc, convertToPixels, isValidInput, uniqueComponentKey } from '../utility'
import { snapDimension } from './useTransformFieldProperties'

const DELETE_KEY = [8, 46]

const useTransformMultipleFieldsProperties = ({ ids, page, dimension, components }) => {
  const dispatch = useDispatch()
  const keys = useMemo(() => ids.map((id) => uniqueComponentKey(...id)), [ids])
  const currentPageWidth = useSelector(({ pages }) => pages.pageWidth)
  const { width: pageWidth, height: pageHeight } = useSelector(
    ({ pages }) => pages.all[page].page_sizes[currentPageWidth]
  )
  const commonPosition = useMemo(() => components[0].rect, [components])

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    const everySame = components.map((c) => c.rect[dimension]).every((v) => v === commonPosition[dimension])
    if (!everySame) {
      setInputValue('mixed')
      return
    }
    const valueConverted = convertToPixels(commonPosition[dimension], chooseMaxPixels(dimension, pageWidth, pageHeight))
    setInputValue(round(valueConverted, 2) || 0)
  }, [commonPosition, dimension])

  const handleOnBlur = useCallback(() => {
    if (!isValidInput(inputValue)) return
    const valueConverted = convertToPerc(inputValue, chooseMaxPixels(dimension, pageWidth, pageHeight))
    const deltaPosition = { ...commonPosition, [dimension]: valueConverted }
    const value = snapDimension(dimension, valueConverted, deltaPosition)
    dispatch(updateMultipleDimensions({ keys, value, dimension }))
  }, [inputValue, commonPosition, dimension, keys])

  const handleOnKeyDown = useCallback((e) => {
    if (e.key === 'Enter') e.target.blur()
  }, [])

  return useCallback(
    () => ({
      value: inputValue,
      onChange: (e) => setInputValue(e.target.value),
      onBlur: () => handleOnBlur(),
      onKeyDown: (e) => handleOnKeyDown(e),
      onKeyUp: (e) => {
        if (DELETE_KEY.includes(e.keyCode)) e.stopPropagation()
      }
    }),
    [inputValue, handleOnBlur, handleOnKeyDown]
  )
}

export default useTransformMultipleFieldsProperties
